/**
 * Noticia Renderer - Specialized module for the detail page
 */
import { sanitizeUrlParam, escapeHtml, getAbsPath } from './utils.js';
import { updateNewsSchema } from './seo.js';
import { setupShare } from './share.js';
import { buildReactionsHTML, setupReactions } from './reactions.js';

function getSlugFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const idParam = params.get('id');
  if (idParam) return idParam;

  // Try query param ?id=slug first, then path: /py/noticia/slug/ or /noticia/slug/
  const pathParts = window.location.pathname.split('/').filter(p => p);
  const noticiaIdx = pathParts.indexOf('noticia');
  if (noticiaIdx !== -1 && pathParts[noticiaIdx + 1]) {
    return pathParts[noticiaIdx + 1];
  }
  return null;
}

/**
 * Initializes the news detail page by fetching data and rendering the specific news item.
 * @returns {Promise<void>}
 */
export async function initializeNoticia() {
  const container = document.getElementById('noticia-content');
  try {
    // 1. Check for preloaded data injected by the serverless function (fastest & supports historial)
    const preloadedScript = document.getElementById('preloaded-news-data');
    if (preloadedScript) {
      const noticia = JSON.parse(preloadedScript.textContent);
      renderSingleNoticia(noticia, container);
      return;
    }

    // 2. Fallback: fetch data
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get('date');
    const dataPath = (dateParam && dateParam !== 'today') 
      ? `/historial/${sanitizeUrlParam(dateParam)}/news.json` 
      : '/data/news.json';

    const response = await fetch(`${dataPath}?t=${Date.now()}`);
    if (!response.ok) throw new Error('Data no encontrada');
    const newsData = await response.json();
    
    renderNoticia(newsData, container);
  } catch (e) {
    console.error('Error al cargar la noticia:', e);
    if (container) {
      container.innerHTML = `
        <div class="noticia-not-found">
          <h1><i class="fas fa-exclamation-triangle"></i> Error de conexión</h1>
          <p>No pudimos cargar los datos. Por favor, intenta nuevamente más tarde.</p>
          <a href="/">Volver al inicio</a>
        </div>
      `;
    }
  }
}

function findNewsBySlug(data, slug) {
  if (!slug || !data) return null;

  const match = n => n?.slug === slug;
  let found;

  if ((found = data.hero?.find(match))) return found;

  if (data.categories) {
    for (const cat of data.categories) {
      if (cat.main?.slug === slug) return cat.main;
      if ((found = cat.secondary?.find(match))) return found;
    }
  }

  return data.technology?.find(match) ||
         data.worldCupNews?.find(match) ||
         data.trending?.mostRead?.find(match) ||
         null;
}

function renderNoticia(data, container) {
  const slug = sanitizeUrlParam(getSlugFromUrl());
  const noticia = findNewsBySlug(data, slug);
  renderSingleNoticia(noticia, container);
}

function renderSingleNoticia(noticia, container) {
  if (!noticia) {
    container.innerHTML = `
      <div class="noticia-not-found">
        <h1><i class="fas fa-exclamation-triangle"></i> Noticia no encontrada</h1>
        <p>No pudimos localizar la noticia solicitada o el parámetro es inválido.</p>
        <a href="/">Volver al inicio</a>
      </div>
    `;
    document.title = 'Noticia no encontrada — Resumen Digital Paraguay';
    return;
  }

  updatePageSEO(noticia);
  
  const params = new URLSearchParams(window.location.search);
  const dateParam = params.get('date') || new Date().toISOString().split('T')[0];
  const absUrl = window.location.origin + window.location.pathname + window.location.search;
  updateNewsSchema(noticia, absUrl, dateParam);

  container.innerHTML = buildNoticiaHTML(noticia);
  setupShare();
  setupReactions(noticia.title, noticia.category);
}

function updatePageSEO(noticia) {
  const fullTitle = noticia.seoTitle || ((noticia.title || 'Noticia') + ' — Resumen Digital Paraguay');
  document.title = fullTitle;

  const metaDescription = noticia.seoDescription || noticia.description || noticia.subtitle || noticia.explanation || noticia.excerpt || '';
  const metaDescShort = metaDescription.length > 160 ? metaDescription.substring(0, 157) + '...' : metaDescription;

  document.querySelector('meta[property="og:title"]')?.setAttribute('content', escapeHtml(noticia.title));
  document.querySelector('meta[property="og:description"]')?.setAttribute('content', escapeHtml(metaDescShort));
  document.querySelector('meta[name="description"]')?.setAttribute('content', escapeHtml(metaDescShort));

  if (noticia.image) {
    const absImage = noticia.image.startsWith('http') ? noticia.image : window.location.origin + '/' + noticia.image;
    document.querySelector('meta[property="og:image"]')?.setAttribute('content', absImage);
    document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', absImage);
  }
}

function buildNoticiaHTML(noticia) {
  let html = '<div class="noticia-header">';

  if (noticia.tag) {
    const tagClass = noticia.tagClass || 'tag--resumen';
    html += `<span class="tag ${tagClass} noticia-tag">${escapeHtml(noticia.tag)}</span>`;
  }

  html += `<h1 class="noticia-title">${escapeHtml(noticia.title)}</h1>`;
  html += '<div class="noticia-meta">';
  if (noticia.category) html += `<div class="noticia-meta-item"><i class="fas fa-folder"></i> ${escapeHtml(noticia.category)}</div>`;
  if (noticia.time) html += `<div class="noticia-meta-item"><i class="fas fa-clock"></i> ${escapeHtml(noticia.time)}</div>`;
  if (noticia.source) {
    html += `
      <div class="noticia-meta-item" style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <span><i class="fas fa-newspaper"></i> Fuente: ${escapeHtml(noticia.source)}</span>
        <div class="share-bar share-bar--inline" id="shareBar" role="complementary" aria-label="Compartir en redes sociales">
          <button class="share-bar__btn share-bar__btn--whatsapp" data-platform="whatsapp" title="WhatsApp">
            <i class="fa-brands fa-whatsapp"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--facebook" data-platform="facebook" title="Facebook">
            <i class="fa-brands fa-facebook-f"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--twitter" data-platform="twitter" title="X (Twitter)">
            <i class="fa-brands fa-x-twitter"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--copy" id="copyUrlBtn" title="Copiar enlace">
            <i class="fas fa-link"></i>
          </button>
        </div>
      </div>`;
  } else {
    // No source: show share bar alone
    html += `
      <div class="noticia-meta-item">
        <div class="share-bar share-bar--inline" id="shareBar" role="complementary" aria-label="Compartir en redes sociales">
          <button class="share-bar__btn share-bar__btn--whatsapp" data-platform="whatsapp" title="WhatsApp">
            <i class="fa-brands fa-whatsapp"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--facebook" data-platform="facebook" title="Facebook">
            <i class="fa-brands fa-facebook-f"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--twitter" data-platform="twitter" title="X (Twitter)">
            <i class="fa-brands fa-x-twitter"></i>
          </button>
          <button class="share-bar__btn share-bar__btn--copy" id="copyUrlBtn" title="Copiar enlace">
            <i class="fas fa-link"></i>
          </button>
        </div>
      </div>`;
  }

  html += '</div></div>';

  if (noticia.image) {
    html += `<img src="${getAbsPath(noticia.image)}" alt="${escapeHtml(noticia.title)}" class="noticia-image" width="900" height="500" loading="lazy" decoding="async">`;
  }

  const sections = [
    { title: 'Resumen', content: noticia.subtitle || noticia.excerpt, icon: 'fa-quote-left' },
    { title: '¿Qué pasó?', content: noticia.explanation, icon: 'fa-book-reader' },
    { title: '¿Por qué importa?', content: noticia.impact, icon: 'fa-bolt' }
  ];

  sections.forEach(sec => {
    if (sec.content && sec.content.trim() !== '') {
      html += `
        <div class="noticia-section">
          <h2 class="noticia-section-title"><i class="fas ${sec.icon}"></i> ${sec.title}</h2>
          <div class="noticia-section-content">${escapeHtml(sec.content)}</div>
        </div>
      `;
    }
  });

  html += `
    <div class="noticia-section" style="margin-top: 3rem; background: #f8f9fc; padding: 2rem; border-radius: 12px; text-align: center; border: 1px solid #e2e6ef;">
      <h3 style="font-family: var(--font-heading); color: var(--brand-navy); margin-bottom: 1rem; font-size: 1.5rem;">
        <i class="fas fa-bolt" style="color: var(--brand-red);"></i> ¿Te interesa estar informado rápidamente?
      </h3>
      <p style="margin-bottom: 1.5rem; color: var(--text-secondary);">
        Sigue explorando nuestros resúmenes y mantente al día con lo más importante en segundos.
      </p>
      <div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
        <a href="/" style="background: var(--brand-red); color: white; padding: 10px 24px; border-radius: 30px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(212, 43, 43, 0.2);">
          <i class="fas fa-home"></i> Ver Portada
        </a>
        <a href="/mundial-2026" style="background: var(--brand-navy); color: white; padding: 10px 24px; border-radius: 30px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(26, 42, 94, 0.2);">
          <i class="fas fa-trophy"></i> Mundial 2026
        </a>
      </div>
    </div>
  `;

  html += `
    <div class="noticia-footer">
      ${buildReactionsHTML()}
      <p style="color: #999; font-size: 0.9rem; margin-top: 2rem;">
        <i class="fas fa-shield-alt"></i> Contenido protegido © 2026 Resumen Digital
      </p>
    </div>
  `;

  return html;
}
